import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  DateRange,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

interface SavingsData {
  total_income: number;
  total_expenses: number;
  net_savings: number;
  savings_rate: number;
  period_name: string;
  // Comparison metrics
  savings_change_amount: number;
  savings_change_percentage: number;
  rate_change: number;
  comparison_period: string;
}

interface SavingsResponse {
  ytd_savings: SavingsData;
  twelve_month_trailing_savings: SavingsData;
  qtd_savings: SavingsData;
  last_quarter_savings: SavingsData;
}

const SavingsMetricsChart: React.FC = () => {
  const [data, setData] = useState<SavingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSavingsData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await analyticsApi.getSavingsMetrics();
        setData(response);
      } catch (err: any) {
        console.error('Error fetching savings metrics:', err);
        setError('Failed to load savings data');
      } finally {
        setLoading(false);
      }
    };

    fetchSavingsData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSavingsRateColor = (savingsRate: number): string => {
    if (savingsRate >= 30) return '#4caf50'; // Green - Excellent
    if (savingsRate >= 20) return '#8bc34a'; // Light Green - Great
    if (savingsRate >= 10) return '#ff9800'; // Orange - Good
    if (savingsRate >= 0) return '#f44336';  // Red - Poor
    return '#d32f2f'; // Dark Red - Negative
  };

  const getSavingsRateLabel = (savingsRate: number): string => {
    if (savingsRate >= 30) return 'Excellent';
    if (savingsRate >= 20) return 'Great';
    if (savingsRate >= 10) return 'Good';
    if (savingsRate >= 0) return 'Fair';
    return 'Poor';
  };

  const renderSavingsCard = (savingsData: SavingsData, icon: React.ReactNode, color: string) => {
    const savingsRateColor = getSavingsRateColor(savingsData.savings_rate);
    const savingsRateLabel = getSavingsRateLabel(savingsData.savings_rate);

    return (
      <Card
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 2,
          border: `2px solid ${color}`,
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 25px ${color}25`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: color, mr: 1.5 }}>
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {savingsData.period_name}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: color, mb: 1 }}>
            {formatCurrency(savingsData.net_savings)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {savingsData.net_savings >= 0 ? (
              <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
            ) : (
              <TrendingDown sx={{ color: 'error.main', fontSize: 20 }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600, color: savingsRateColor }}>
              {Math.abs(savingsData.savings_rate).toFixed(1)}%
            </Typography>
            <Chip
              label={savingsRateLabel}
              size="small"
              sx={{
                backgroundColor: savingsRateColor,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          </Box>

          {/* Comparison metrics */}
          {savingsData.comparison_period && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                vs {savingsData.comparison_period}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {savingsData.savings_change_amount >= 0 ? (
                  <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
                ) : (
                  <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: savingsData.savings_change_amount >= 0 ? 'success.main' : 'error.main'
                  }}
                >
                  {savingsData.savings_change_amount >= 0 ? '+' : ''}{formatCurrency(savingsData.savings_change_amount)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: savingsData.savings_change_percentage >= 0 ? 'success.main' : 'error.main'
                  }}
                >
                  ({savingsData.savings_change_percentage >= 0 ? '+' : ''}{savingsData.savings_change_percentage.toFixed(1)}%)
                </Typography>
              </Box>
              {savingsData.rate_change !== 0 && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: savingsData.rate_change >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 500
                  }}
                >
                  Rate: {savingsData.rate_change >= 0 ? '+' : ''}{savingsData.rate_change.toFixed(1)}pp
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={Math.min(Math.max(Math.abs(savingsData.savings_rate), 0), 100)}
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 2,
            backgroundColor: `${savingsRateColor}20`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: savingsRateColor,
              borderRadius: 4,
            },
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Income
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
              {formatCurrency(savingsData.total_income)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Expenses
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
              {formatCurrency(savingsData.total_expenses)}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No savings data available
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SavingsIcon sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Savings Overview
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', lg: '1 1 22%' }, minWidth: 280 }}>
          {renderSavingsCard(
            data.ytd_savings,
            <DateRange sx={{ fontSize: 24 }} />,
            '#1976d2' // Blue
          )}
        </Box>
        
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', lg: '1 1 22%' }, minWidth: 280 }}>
          {renderSavingsCard(
            data.twelve_month_trailing_savings,
            <AccountBalance sx={{ fontSize: 24 }} />,
            '#388e3c' // Green
          )}
        </Box>
        
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', lg: '1 1 22%' }, minWidth: 280 }}>
          {renderSavingsCard(
            data.qtd_savings,
            <TrendingUp sx={{ fontSize: 24 }} />,
            '#f57c00' // Orange
          )}
        </Box>
        
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', lg: '1 1 22%' }, minWidth: 280 }}>
          {renderSavingsCard(
            data.last_quarter_savings,
            <TrendingDown sx={{ fontSize: 24 }} />,
            '#7b1fa2' // Purple
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          <strong>Savings Rate Guide:</strong> Excellent (30%+) • Great (20-29%) • Good (10-19%) • Fair (0-9%) • Poor (&lt;0%)
        </Typography>
      </Box>
    </Box>
  );
};

export default SavingsMetricsChart;